import Link from "next/link"
import { useRouter } from "next/router"
import { useMemo, useRef, useState } from "react"

import { url } from "@/lib/url"
import { useOrgPropertySearchQuery } from "@/v2/graphql/public-generated-rq"
import { Combobox } from "@headlessui/react"
import { Search, twMerge } from "@lumen-energy/design-system"

type SearchSource = {
  id: string
  title: string
  term: string
  url: string
}

export function PropertySearch({
  orgSlug,
  placeholder,
  className,
  dark,
  showLoadingSpinner,
}: {
  orgSlug: string
  placeholder?: string
  className?: string
  dark?: boolean
  showLoadingSpinner?: boolean
}) {
  const [query, setQuery] = useState("")
  const [inputFocused, setInputFocused] = useState<boolean>(false)
  const [selectedPropertyUrl, setSelectedPropertyUrl] = useState<SearchSource | null>(
    null,
  )
  const searchInputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()

  const { data, isLoading } = useOrgPropertySearchQuery(
    { orgSlug },
    { enabled: Boolean(orgSlug) },
  )

  const handleEnterKeypress = () => {
    // Will only be invoked when a user selects a value by pressing 'Enter'
    setTimeout(() => {
      // Property has been clicked; close the Combobox, clear query and navigate to property
      // setTimeout is needed to blur AFTER `Combobox` does its own focus handling
      if (searchInputRef.current && selectedPropertyUrl) {
        setQuery("")
        searchInputRef.current.blur()
        router.push(selectedPropertyUrl.url)
      }
    })
  }

  const simulateMouseClick = (element: HTMLElement) => {
    const events = ["mousedown", "click", "mouseup"]
    events.forEach((mouseEventType) =>
      element.dispatchEvent(
        new MouseEvent(mouseEventType, {
          view: window,
          bubbles: true,
          cancelable: true,
          buttons: 1,
        }),
      ),
    )
  }

  const sources: SearchSource[] = useMemo(() => {
    return (data?.orgBySlug?.siteSearchTerms ?? []).map(
      ({ pid: propertyPid, propertyName, address }) => {
        const title = propertyName ? `${propertyName}, ${address}` : address
        return {
          id: propertyPid,
          title,
          term: title.toLowerCase(),
          url: url(["/[orgSlug]/properties/[propertyPid]", { orgSlug, propertyPid }]),
        }
      },
    )
  }, [data?.orgBySlug?.siteSearchTerms, orgSlug])

  const filteredSources = useMemo(() => {
    return query === ""
      ? sources.slice(0, 50)
      : sources
          .filter((item) => {
            return item.term.includes(query.toLowerCase())
          })
          .slice(0, 50)
  }, [query, sources])

  return (
    <Combobox
      disabled={isLoading}
      value={undefined}
      onChange={(value: SearchSource) => {
        if (value?.url) {
          // This url is used to navigate to the selected property url if the user has used 'Enter' to select
          setSelectedPropertyUrl(value)
        }
      }}
    >
      <div className={twMerge("relative grow", className)}>
        <Combobox.Input
          displayValue={() => query}
          onChange={(event) => setQuery(event.target.value)}
          className="grow"
          as={Search}
          size="large"
          placeholder={inputFocused ? "" : placeholder}
          dark={dark}
          showLoadingSpinner={showLoadingSpinner}
          ref={searchInputRef}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleEnterKeypress()
            }
          }}
        />
        <Combobox.Options className="absolute z-50 mt-1 inline-block max-h-80 w-full overflow-auto rounded bg-white p-2 ring-1 ring-grey-300">
          {filteredSources.length === 0 && query !== "" ? (
            <div className="text-gray-700 text-body1 relative inline-block w-full cursor-default p-2">
              Nothing found
            </div>
          ) : (
            filteredSources.map((item) => (
              <Combobox.Option
                key={item.id}
                className={({ active }) =>
                  `relative rounded hover:bg-brand-primary-200 ${
                    active && "bg-brand-primary-200"
                  }`
                }
                value={item}
              >
                <Link legacyBehavior href={item?.url} passHref>
                  <a
                    className="text-body1 inline-block w-full p-2"
                    target="_self"
                    onClick={() => {
                      // Combobox needs to close after click, so we simulate a click outside the field
                      // For reference: https://github.com/tailwindlabs/headlessui/discussions/2207#discussioncomment-7509795
                      simulateMouseClick(document.body)

                      // Clear query after click
                      setQuery("")
                    }}
                  >
                    {item.title}
                  </a>
                </Link>
              </Combobox.Option>
            ))
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  )
}
